import { useState, useEffect, useMemo, useRef } from 'react'
import { supabase } from '../supabase'
import { publishReadingPlan } from '../hooks/useSermons'
import { CircleLogoOrInitials, CIRCLE_PASTELS } from './ExploreTab'
import YouTubeEmbed, { fmtClock } from './YouTubeEmbed'
import ReadingPlanReviewSheet from './ReadingPlanReviewSheet'
import ReadingPlanDaySheet from './ReadingPlanDaySheet'

// ── SERMON SHEET ───────────────────────────────────────────────────────────
// Full-screen bottom sheet, rebuilt per docs/designs/SermonSheet.dc.html /
// SermonSheetSummary.dc.html (docs/FIX-SERMON-CLIPS.md §3) — replaces the
// old collapsible-sections design entirely: pinned mini-player, one Bible
// references list with kind tags, a Summary tab, no red anywhere, no public
// Edit link, no "No summary available".
//
// Opens in two modes:
//   normal mode — from the Sermons tab / This Week / search / notifications:
//     starts at 0 (or startAtSeconds if given, e.g. resuming where left off)
//   clip mode — from the verse sheet's "Open full sermon →": starts at
//     startAtSeconds (the clip's current playback time), shows the
//     "Started at [ref] · mm:ss" banner
//
// Props:
//   sermon         — { id, church_id, title, series, preacher, date, youtube_id,
//                      youtube_url, duration, ai_summary, display_title }
//   churchName     — display name for the church/source
//   churchLogo     — logo url (optional)
//   originVerse    — { book, chapter, verse } | null — present = clip mode
//   startAtSeconds — seconds to start the player at (clip mode: the clip's
//                    current time; normal mode: a resume point, or null for 0)
//   isAdmin        — elder/admin/super_admin: shows ⋯ menu (Edit title) and
//                    the reading-plan generate/review/publish controls
//   userId         — current user's id (reading-plan generate/save/follow calls)
//   initialTab         — 'refs' | 'summary', for restoring the Back-to-sermon
//                         round trip (docs/FIX-SERMON-CLIPS.md §3b); defaults
//                         to the usual clip-mode-based default if omitted
//   initialScrollTop   — scroll position to restore, same round trip
//   onClose()          — close the sheet (returns to the origin verse sheet
//                         instead, if one is open behind this sheet)
//   onOpenRefAndPause(book, chapter, verse, snapshot) — "Open in Bible": close
//     + navigate the reader to a ref, handing the caller a snapshot
//     ({ activeTab, scrollTop, pausedAtSeconds }) so a "Back to sermon" bar
//     can reopen this exact sheet state later
//   onShare(sermon)    — optional; shows a Share button when provided

const GOLD = 'var(--gold)'
const SERIF = "'Source Serif 4', Georgia, serif"
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif"

const BOOK_ORDER = [
  'Genesis','Exodus','Leviticus','Numbers','Deuteronomy','Joshua','Judges','Ruth',
  '1 Samuel','2 Samuel','1 Kings','2 Kings','1 Chronicles','2 Chronicles','Ezra',
  'Nehemiah','Esther','Job','Psalms','Proverbs','Ecclesiastes','Song of Solomon',
  'Isaiah','Jeremiah','Lamentations','Ezekiel','Daniel','Hosea','Joel','Amos',
  'Obadiah','Jonah','Micah','Nahum','Habakkuk','Zephaniah','Haggai','Zechariah',
  'Malachi','Matthew','Mark','Luke','John','Acts','Romans','1 Corinthians',
  '2 Corinthians','Galatians','Ephesians','Philippians','Colossians',
  '1 Thessalonians','2 Thessalonians','1 Timothy','2 Timothy','Titus','Philemon',
  'Hebrews','James','1 Peter','2 Peter','1 John','2 John','3 John','Jude','Revelation',
]
const bookIndex = (b) => { const i = BOOK_ORDER.indexOf(b); return i === -1 ? 999 : i }

function fmtDate(d) {
  if (!d) return ''
  try { return new Date(d + (d.length <= 10 ? 'T00:00:00' : '')).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) }
  catch { return d }
}
// sermons.duration is sometimes seconds (number/numeric string), sometimes
// already formatted ("38:12") from an older upload path — tolerate both.
function durationToSeconds(duration) {
  if (!duration) return 0
  const str = String(duration).trim()
  if (str.includes(':')) {
    const parts = str.split(':').map(Number)
    return parts.length === 3 ? parts[0] * 3600 + parts[1] * 60 + parts[2] : parts[0] * 60 + (parts[1] || 0)
  }
  const n = Number(str)
  return Number.isFinite(n) ? n : 0
}
function fmtDurationLong(duration) {
  const totalSeconds = durationToSeconds(duration)
  if (totalSeconds <= 0) return ''
  const hrs = Math.floor(totalSeconds / 3600)
  const mins = Math.round((totalSeconds % 3600) / 60)
  if (hrs > 0) return `${hrs} hr${hrs === 1 ? '' : 's'}${mins ? ` ${mins} min` : ''}`
  return `${mins} min`
}
function kindTagLabel(kind) {
  return kind === 'main_passage' ? 'Main passage' : kind === 'moment' ? 'Key moment' : 'Mentioned'
}

// Minimal inline markdown for AI-generated summary text (**bold**, *italic*/
// _italic_) — no other formatting, no library needed for just these two.
function renderInlineMarkdown(text) {
  if (!text) return text
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>
    if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) return <em key={i}>{part.slice(1, -1)}</em>
    return part
  })
}

export default function FullSermonSheet({
  sermon, churchName, churchLogo, originVerse, startAtSeconds, initialTab, initialScrollTop, isAdmin, userId, userChurchId,
  onClose, onOpenRefAndPause, onShare,
  // §3d: true when opened from inside another sheet (which stays mounted,
  // dimmed, behind this one — that sheet applies its own dimming via
  // StackedSheet.behindStyle; this component just needs to stop its own
  // backdrop from closing on an outside tap, nudging instead.
  stacked, backLabel,
}) {
  const [nudge, setNudge] = useState(false)
  const [refs, setRefs] = useState(null)          // null = loading
  const [row, setRow] = useState(null)            // canonical sermon row (fills prop gaps)
  const [editingDisplayTitle, setEditingDisplayTitle] = useState(false)
  const [displayTitleDraft, setDisplayTitleDraft] = useState('')
  const [savingDisplayTitle, setSavingDisplayTitle] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const clipMode = !!originVerse
  const [activeTab, setActiveTab] = useState(initialTab || (clipMode ? 'refs' : 'summary'))
  const [expandedNoteId, setExpandedNoteId] = useState(null)

  // Reading plan — undefined = loading, null = none generated yet.
  const [plan, setPlan] = useState(undefined)
  const [planDays, setPlanDays] = useState([])
  const [follow, setFollow] = useState(null)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [dayOpen, setDayOpen] = useState(null)   // initial day number, or null when closed

  // Player state — current playback time drives the "current reference" row
  // highlight and is handed to openInBible()'s snapshot / the mini-bar's label.
  const [currentTime, setCurrentTime] = useState(startAtSeconds || 0)
  // Starts false to match autoplay={false} below — the player only actually
  // starts once the user taps play, and `playing` is driven entirely by
  // YouTubeEmbed's onPlayingChange from then on, not guessed locally.
  const [playing, setPlaying] = useState(false)
  // True from the first real PLAYING event onward — gates the mini-bar so it
  // doesn't show before the sermon has been started (docs/FIX-SERMON-CLIPS.md §3c).
  const [hasStarted, setHasStarted] = useState(false)
  const playerApiRef = useRef(null)
  const scrollRef = useRef(null)
  const playerWrapRef = useRef(null)
  const [playerCollapsed, setPlayerCollapsed] = useState(false)

  const fetchPlan = useMemo(() => async () => {
    if (!sermon?.id) { setPlan(null); return }
    const { data } = await supabase
      .from('reading_plans')
      .select('*, reading_plan_days(*)')
      .eq('sermon_id', sermon.id)
      .maybeSingle()
    if (!data) { setPlan(null); setPlanDays([]); return }
    const { reading_plan_days, ...planRow } = data
    setPlan(planRow)
    setPlanDays((reading_plan_days || []).slice().sort((a, b) => a.day_number - b.day_number))
  }, [sermon?.id])

  useEffect(() => { fetchPlan() }, [fetchPlan])

  useEffect(() => {
    let cancelled = false
    if (!plan || plan.status !== 'published' || !userId) { setFollow(null); return }
    supabase.from('reading_plan_follows').select('*').eq('plan_id', plan.id).eq('user_id', userId).maybeSingle()
      .then(({ data }) => { if (!cancelled) setFollow(data || null) })
    return () => { cancelled = true }
  }, [plan, userId])

  async function openDay(dayNumber) {
    let f = follow
    if (!f && userId && plan) {
      const { data } = await supabase.from('reading_plan_follows')
        .upsert({ plan_id: plan.id, user_id: userId, last_opened_day: dayNumber }, { onConflict: 'plan_id,user_id' })
        .select().single()
      f = data
      setFollow(data || null)
    }
    setDayOpen(dayNumber)
  }

  async function publishNow() {
    if (!plan) return
    await publishReadingPlan(plan.id, userId)
    fetchPlan()
  }

  async function saveDisplayTitle() {
    if (!sermon?.id || savingDisplayTitle) return
    setSavingDisplayTitle(true)
    const value = displayTitleDraft.trim() || null
    const { error } = await supabase.from('sermons').update({ display_title: value }).eq('id', sermon.id)
    setSavingDisplayTitle(false)
    if (!error) { setRow(r => ({ ...r, display_title: value })); setEditingDisplayTitle(false) }
  }

  // Merge the fetched canonical row over the (possibly lightweight) prop so the
  // sheet works whether opened from a full sermon object or a verse-sheet clip.
  const sm = useMemo(() => ({ ...(sermon || {}), ...(row || {}) }), [sermon, row])

  useEffect(() => {
    let cancelled = false
    if (!sermon?.id) { setRefs([]); return }
    ;(async () => {
      let sres = await supabase.from('sermons')
        .select('id, church_id, title, series, preacher, date, youtube_id, youtube_url, duration, ai_summary, display_title')
        .eq('id', sermon.id).single()
      if (sres.error && /display_title/i.test(sres.error.message || '')) {
        sres = await supabase.from('sermons')
          .select('id, church_id, title, series, preacher, date, youtube_id, youtube_url, duration, ai_summary')
          .eq('id', sermon.id).single()
      }
      if (sres.error && /ai_summary/i.test(sres.error.message || '')) {
        sres = await supabase.from('sermons')
          .select('id, church_id, title, series, preacher, date, youtube_id, youtube_url, duration')
          .eq('id', sermon.id).single()
      }
      if (!cancelled && sres.data) {
        setRow(sres.data)
        if (!sres.data.ai_summary && !clipMode) setActiveTab('refs')
      }

      const { data } = await supabase.from('sermon_refs').select('*').eq('sermon_id', sermon.id)
      if (cancelled) return
      const rows = (data || []).slice().sort((a, b) =>
        bookIndex(a.book) - bookIndex(b.book) || (a.chapter - b.chapter) || (a.verse - b.verse) || (a.timestamp_s || 0) - (b.timestamp_s || 0))
      setRefs(rows)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sermon?.id])

  // Sort refs by time for the "current reference" lookup and the reference
  // list itself (time order, per the doc — not grouped by book anymore).
  const timeOrderedRefs = useMemo(() => {
    if (!refs) return []
    return refs.slice().sort((a, b) => (a.timestamp_s ?? Infinity) - (b.timestamp_s ?? Infinity))
  }, [refs])

  const currentRef = useMemo(() => {
    let cur = null
    for (const r of timeOrderedRefs) {
      if (r.timestamp_s == null) continue
      if (r.timestamp_s <= currentTime) cur = r
      else break
    }
    return cur
  }, [timeOrderedRefs, currentTime])

  const mainPassageRef = useMemo(() => (refs || []).find(r => r.kind === 'main_passage') || null, [refs])

  // Restore scroll position when reopened via the "Back to sermon" bar
  // (docs/FIX-SERMON-CLIPS.md §3b) — runs once on mount, before the paint the
  // user sees, so there's no visible jump.
  useEffect(() => {
    if (initialScrollTop && scrollRef.current) scrollRef.current.scrollTop = initialScrollTop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Pin the player into a mini-bar once the user scrolls it out of view.
  useEffect(() => {
    const scrollEl = scrollRef.current
    const wrapEl = playerWrapRef.current
    if (!scrollEl || !wrapEl) return
    const onScroll = () => {
      const wrapBottom = wrapEl.offsetTop + wrapEl.offsetHeight
      setPlayerCollapsed(scrollEl.scrollTop > wrapBottom - 40)
    }
    scrollEl.addEventListener('scroll', onScroll)
    return () => scrollEl.removeEventListener('scroll', onScroll)
  }, [])

  const thumb = sm.youtube_id ? `https://img.youtube.com/vi/${sm.youtube_id}/hqdefault.jpg` : null
  const durationSeconds = durationToSeconds(sm.duration)
  const durationLabel = fmtDurationLong(sm.duration)
  const playerStart = startAtSeconds != null ? startAtSeconds : 0
  const hasSummary = !!sm.ai_summary
  const bigIdea = sm.big_idea || ''
  const takeaways = Array.isArray(sm.takeaways) ? sm.takeaways : null

  // Never a random reference, never "Sermon · [date]" — display_title, then
  // the main passage, then "Sunday sermon" (docs/FIX-SERMON-CLIPS.md §0.3/§3c).
  // Local to this sheet rather than the shared displayTitleOf() helper, which
  // other call sites (Sermons tab cards, This Week) use without sermon_refs.kind loaded.
  const mainPassageLabel = mainPassageRef
    ? `${mainPassageRef.book} ${mainPassageRef.chapter}:${mainPassageRef.verse}${mainPassageRef.end_verse && mainPassageRef.end_verse !== mainPassageRef.verse ? `–${mainPassageRef.end_verse}` : ''}`
    : null
  const sheetTitle = sm.display_title || mainPassageLabel || "Sunday sermon"

  // Deterministic colour per church (no stored church colour exists) —
  // mirrors the church-circle palette used elsewhere so the same church
  // always lands on the same pastel across different sermon sheets.
  const avatarPastel = useMemo(() => {
    const key = String(sm.church_id || churchName || '')
    let hash = 0
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0
    return CIRCLE_PASTELS[hash % CIRCLE_PASTELS.length]
  }, [sm.church_id, churchName])

  // "Open in Bible" (chip or a reference row's pill) — docs/FIX-SERMON-CLIPS.md
  // §3b. Hands the caller a snapshot of where this sheet was so a "Back to
  // sermon" bar can reopen it exactly as it was.
  function openInBible(book, chapter, verse) {
    onOpenRefAndPause?.(book, chapter, verse, {
      activeTab, scrollTop: scrollRef.current?.scrollTop || 0, pausedAtSeconds: currentTime,
    })
  }

  // Stacked (§3d): tapping outside never closes — nudge instead. Unstacked
  // (opened from a page): unchanged, tapping the backdrop closes as before.
  function onBackdropClick(e) {
    if (e.target !== e.currentTarget) return
    if (!stacked) { onClose?.(); return }
    setNudge(false)
    requestAnimationFrame(() => setNudge(true))
  }

  return (
    <div style={S.backdrop} onClick={onBackdropClick}>
      <style>{'@keyframes fss-up{from{transform:translateY(100%)}to{transform:none}} @keyframes fss-nudge{0%{transform:translateY(0)}35%{transform:translateY(-14px)}70%{transform:translateY(3px)}100%{transform:translateY(0)}}'}</style>
      <div style={{
        ...S.sheet, animation: nudge ? 'fss-nudge .42s ease' : S.sheet.animation,
        transform: dayOpen != null ? 'translateY(-10px) scale(0.93)' : 'none',
        filter: dayOpen != null ? 'brightness(0.82)' : 'none',
        borderRadius: dayOpen != null ? 26 : S.sheet.borderRadius,
        transition: `${S.sheet.transition ? S.sheet.transition + ', ' : ''}transform .42s cubic-bezier(.2,.8,.2,1), filter .42s ease, border-radius .42s ease`,
      }} onAnimationEnd={() => setNudge(false)}
        {...(stacked && backLabel ? { 'aria-label': `Sermon, stacked over ${backLabel}` } : {})}>
        {/* Header */}
        <div style={S.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ ...S.avatar, background: avatarPastel.bg, color: avatarPastel.ink }}>
              <CircleLogoOrInitials url={churchLogo} name={churchName}/>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{churchName || 'Church'}</div>
              <div style={{ fontSize: 12, color: 'var(--stone)' }}>{[fmtDate(sm.date), durationLabel].filter(Boolean).join(' · ')}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, position: 'relative' }}>
            {onShare && (
              <button onClick={() => onShare(sermon)} title="Share" style={S.iconBtn}>
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7M16 6l-4-4-4 4M12 2v14"/></svg>
              </button>
            )}
            {isAdmin && (
              <>
                <button onClick={() => setMenuOpen(v => !v)} title="More" style={S.iconBtn}>⋯</button>
                {menuOpen && (
                  <div style={S.menuPopover} onClick={e => e.stopPropagation()}>
                    <button onClick={() => { setDisplayTitleDraft(sm.display_title || ''); setEditingDisplayTitle(true); setMenuOpen(false) }} style={S.menuItem}>Edit title</button>
                  </div>
                )}
              </>
            )}
            <button onClick={onClose} title={stacked && backLabel ? `Close sermon, back to ${backLabel}` : "Close"} aria-label={stacked && backLabel ? `Close sermon, back to ${backLabel}` : "Close"} style={S.iconBtn}>✕</button>
          </div>
        </div>

        {/* Pinned mini-bar — shown once the player scrolls out of view, and
            only once the sermon has actually been started (docs/FIX-SERMON-CLIPS.md
            §3c); stays up through both playing and paused after that. */}
        {playerCollapsed && hasStarted && sm.youtube_id && (
          <div style={S.miniBar} onClick={() => { scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }) }}>
            {thumb && <img src={thumb} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}/>}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentRef ? `${currentRef.book} ${currentRef.chapter}:${currentRef.verse}` : sheetTitle}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{playing ? 'Playing' : 'Paused'} · {fmtClock(currentTime)}{durationSeconds > 0 ? ` of ${fmtClock(durationSeconds)}` : ''}</div>
            </div>
            <button onClick={e => { e.stopPropagation(); playing ? playerApiRef.current?.pause() : playerApiRef.current?.play() }}
              style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 16, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {playing ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" aria-hidden="true"><path d="M7 4v16M17 4v16"/></svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
              )}
            </button>
          </div>
        )}

        <div style={S.scroll} ref={scrollRef}>
          {/* Player */}
          <div ref={playerWrapRef}>
            {sm.youtube_id ? (
              <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 16, overflow: 'hidden', background: '#000', marginBottom: clipMode ? 10 : 20 }}>
                <YouTubeEmbed ref={playerApiRef} videoId={sm.youtube_id} start={playerStart} autoplay={false}
                  onTimeUpdate={setCurrentTime}
                  onPlayingChange={isPlaying => { setPlaying(isPlaying); if (isPlaying) setHasStarted(true) }}
                />
              </div>
            ) : (
              <div style={{ width: '100%', aspectRatio: '16/9', borderRadius: 16, background: 'var(--border)', marginBottom: 20 }}/>
            )}
            {clipMode && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--lavender)', color: 'var(--lavender-ink)', borderRadius: 14, padding: '9px 12px', fontSize: 12.5, fontWeight: 700, marginBottom: 20 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 4, background: '#7C6BC4' }}/>
                  Started at {originVerse.book} {originVerse.chapter}:{originVerse.verse} · {fmtClock(playerStart)}
                </span>
                <span style={{ textDecoration: 'underline', whiteSpace: 'nowrap', cursor: 'pointer' }}
                  onClick={() => { playerApiRef.current?.seekTo(0); setCurrentTime(0) }}>From the start</span>
              </div>
            )}
          </div>

          {/* Title block */}
          {editingDisplayTitle ? (
            <div style={{ marginBottom: 8 }}>
              <input autoFocus value={displayTitleDraft} onChange={e => setDisplayTitleDraft(e.target.value)}
                placeholder="e.g. Disciple as a discipler"
                style={{ width: '100%', fontFamily: SERIF, fontSize: 23, color: 'var(--ink)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '8px 10px', boxSizing: 'border-box', outline: 'none' }}/>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={saveDisplayTitle} disabled={savingDisplayTitle} style={{ ...S.outlineBtn, background: 'var(--gold)', border: 'none' }}>{savingDisplayTitle ? 'Saving…' : 'Save'}</button>
                <button onClick={() => setEditingDisplayTitle(false)} disabled={savingDisplayTitle} style={S.outlineBtn}>Cancel</button>
              </div>
            </div>
          ) : (
            <h1 style={{ fontFamily: SANS, fontWeight: 800, fontSize: 23, lineHeight: 1.2, color: 'var(--ink)', margin: '0 0 10px' }}>
              {sheetTitle}
            </h1>
          )}

          {/* Chips */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
            {mainPassageRef && (
              <button onClick={() => openInBible(mainPassageRef.book, mainPassageRef.chapter, mainPassageRef.verse)} style={S.chipLavender}>
                <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5zM4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/></svg>
                {mainPassageRef.book} {mainPassageRef.chapter}:{mainPassageRef.verse}{mainPassageRef.end_verse && mainPassageRef.end_verse !== mainPassageRef.verse ? `–${mainPassageRef.end_verse}` : ''}
              </button>
            )}
            {sm.series && <span style={S.chipWhite}>Series: {sm.series}</span>}
            {sm.preacher && <span style={{ fontSize: 12.5, color: 'var(--stone)', fontWeight: 600 }}>{sm.preacher}</span>}
          </div>

          {/* Row: 5-day devotional (no Save — nowhere to see saved sermons yet) */}
          {(plan === undefined ? false : (plan && plan.status === 'published') || isAdmin) && (
            <div style={{ marginBottom: 18 }}>
              {plan && plan.status === 'published' ? (
                <button onClick={() => openDay(follow?.last_opened_day || 1)} style={S.devoTile}>
                  <span style={S.devoTileIcon}>
                    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5zM4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/></svg>
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', flex: 1, textAlign: 'left' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>5-day devotional</span>
                    <span style={{ fontSize: 11.5, color: 'var(--stone)' }}>Day {follow?.last_opened_day || 1} · 5 min</span>
                  </span>
                  <span style={{ color: '#7A5A12', fontSize: 14 }}>›</span>
                </button>
              ) : isAdmin ? (
                plan === null ? (
                  <button onClick={() => setReviewOpen(true)} style={S.outlineBtn}>Create devotional</button>
                ) : plan.status === 'draft' ? (
                  <button onClick={() => setReviewOpen(true)} style={S.outlineBtn}>Continue draft devotional</button>
                ) : (
                  <button onClick={publishNow} style={S.outlineBtn}>Publish devotional</button>
                )
              ) : null}
            </div>
          )}

          {/* Segmented tabs */}
          <div style={S.segmented}>
            <button onClick={() => setActiveTab('refs')} style={{ ...S.segmentBtn, ...(activeTab === 'refs' ? S.segmentOn : {}) }}>Bible references · {refs?.length ?? 0}</button>
            {hasSummary && <button onClick={() => setActiveTab('summary')} style={{ ...S.segmentBtn, ...(activeTab === 'summary' ? S.segmentOn : {}) }}>Summary</button>}
          </div>

          {activeTab === 'refs' && (
            <div style={S.refsCard}>
              {refs === null ? (
                <div style={{ fontSize: 13, color: 'var(--stone)', padding: '12px 2px' }}>Loading…</div>
              ) : timeOrderedRefs.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--stone)', padding: '12px 2px' }}>No references found.</div>
              ) : timeOrderedRefs.map((r, i) => {
                const isCurrent = currentRef?.id === r.id
                const noteExpanded = expandedNoteId === r.id
                return (
                  <div key={r.id} style={{ display: 'flex', gap: 12, padding: '12px 0', borderTop: i > 0 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}
                    onClick={() => { if (r.timestamp_s != null) { playerApiRef.current?.seekTo(r.timestamp_s); setCurrentTime(r.timestamp_s) } }}>
                    <span style={{
                      display: 'flex', alignItems: 'center', gap: 5, alignSelf: 'flex-start', justifyContent: 'center',
                      minWidth: 62, boxSizing: 'border-box', borderRadius: 8, padding: '4px 7px', fontSize: 12, fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums', background: isCurrent ? '#7C6BC4' : 'var(--cream)', color: isCurrent ? '#fff' : 'var(--ink)',
                    }}>
                      {isCurrent ? (
                        <span style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 10 }}>
                          <span style={{ width: 2, height: 6, background: '#fff', borderRadius: 1 }}/>
                          <span style={{ width: 2, height: 10, background: '#fff', borderRadius: 1 }}/>
                          <span style={{ width: 2, height: 4, background: '#fff', borderRadius: 1 }}/>
                        </span>
                      ) : r.timestamp_s != null && (
                        <svg aria-hidden="true" width="8" height="8" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M8 5v14l11-7z"/></svg>
                      )}
                      {r.timestamp_s != null ? fmtClock(r.timestamp_s) : '—'}
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: SANS, fontWeight: 800, fontSize: 14, color: 'var(--ink)' }}>{r.verse_ref || `${r.book} ${r.chapter}:${r.verse}`}</span>
                        <span style={{ background: r.kind === 'main_passage' ? 'var(--lavender)' : '#EFEAE2', color: r.kind === 'main_passage' ? 'var(--lavender-ink)' : '#4A4239', borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{kindTagLabel(r.kind)}</span>
                      </span>
                      {r.notes && (
                        <span onClick={e => { e.stopPropagation(); setExpandedNoteId(noteExpanded ? null : r.id) }}
                          style={{ fontSize: 12.5, lineHeight: 1.4, color: '#3A332C', ...(noteExpanded ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}>
                          {r.notes}
                        </span>
                      )}
                      <button onClick={e => { e.stopPropagation(); openInBible(r.book, r.chapter, r.verse) }}
                        style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 5, marginTop: 4, border: '1.5px solid var(--gold)', background: 'none', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 800, color: 'var(--gold-ink)', cursor: 'pointer', fontFamily: SANS }}>
                        <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5zM4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/></svg>
                        Open in Bible
                      </button>
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          {activeTab === 'summary' && hasSummary && (
            <div style={S.refsCard}>
              {bigIdea && <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gold-ink)', marginBottom: 8 }}>The big idea</div>}
              {bigIdea && <div style={{ fontFamily: SANS, fontSize: 15, fontWeight: 700, lineHeight: 1.45, color: 'var(--ink)', marginBottom: 14 }}>{renderInlineMarkdown(bigIdea)}</div>}
              {sm.ai_summary && <div style={{ fontFamily: SANS, fontSize: 13.5, color: '#3A332C', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{renderInlineMarkdown(sm.ai_summary)}</div>}
              {takeaways && takeaways.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gold-ink)', marginBottom: 10 }}>Three takeaways</div>
                  {takeaways.map((t, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                      <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 11, background: 'var(--lavender)', color: 'var(--lavender-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>{i + 1}</span>
                      <span style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink)' }}>{renderInlineMarkdown(t)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {reviewOpen && (
        <ReadingPlanReviewSheet
          sermon={sm}
          sermonRefs={refs || []}
          existingPlan={plan ? { ...plan, days: planDays } : null}
          userId={userId}
          onClose={() => setReviewOpen(false)}
          onSaved={() => { setReviewOpen(false); fetchPlan() }}
        />
      )}

      {dayOpen != null && plan && (
        <ReadingPlanDaySheet
          plan={plan}
          days={planDays}
          follow={follow}
          initialDay={dayOpen}
          sermon={sm}
          userId={userId}
          userChurchId={userChurchId}
          onClose={() => setDayOpen(null)}
          onOpenSermon={() => setDayOpen(null)}
          onFollowChange={(f) => setFollow(f)}
          stacked
        />
      )}
    </div>
  )
}

const S = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', fontFamily: SANS },
  sheet: { background: 'var(--cream)', width: '100%', maxWidth: 480, margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column', animation: 'fss-up 300ms cubic-bezier(0.16,1,0.3,1)', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' },
  header: { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border)', minHeight: 54, background: 'var(--cream)' },
  iconBtn: { background: 'var(--white)', border: 'none', color: 'var(--ink)', width: 34, height: 34, borderRadius: 999, cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  menuPopover: { position: 'absolute', top: 40, right: 0, background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: 6, zIndex: 10, minWidth: 140 },
  menuItem: { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '9px 10px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', cursor: 'pointer', fontFamily: SANS },
  miniBar: { flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, background: '#1F1B16', padding: '8px 14px', cursor: 'pointer' },
  scroll: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '18px 18px calc(env(safe-area-inset-bottom) + 32px)' },
  avatar: { flexShrink: 0, width: 34, height: 34, borderRadius: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12, overflow: 'hidden' },
  outlineBtn: { background: 'transparent', border: `1px solid ${GOLD}`, color: 'var(--gold-ink)', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: SANS },
  chipLavender: { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--lavender)', color: 'var(--lavender-ink)', border: 'none', borderRadius: 999, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: SANS },
  chipWhite: { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--white)', color: '#4A4239', borderRadius: 999, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' },
  devoTile: { width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--white)', border: 'none', borderRadius: 16, padding: '10px 12px', cursor: 'pointer', fontFamily: SANS },
  devoTileIcon: { width: 32, height: 32, borderRadius: 10, background: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  segmented: { display: 'flex', background: '#EDE6DB', borderRadius: 999, padding: 4, marginBottom: 14 },
  segmentBtn: { flex: 1, textAlign: 'center', padding: '8px 0', borderRadius: 999, fontSize: 13, fontWeight: 700, background: 'transparent', color: 'var(--stone)', border: 'none', cursor: 'pointer', fontFamily: SANS },
  segmentOn: { background: 'var(--white)', color: 'var(--ink)', fontWeight: 800, boxShadow: '0 1px 3px rgba(31,27,22,0.08)' },
  refsCard: { display: 'flex', flexDirection: 'column', background: 'var(--white)', borderRadius: 20, padding: '0 14px' },
}
